import { NgZone } from '@angular/core';
import { HotelRoomApiModel } from '@era/inroom-sdk';
import { Store } from '@ngrx/store';
import { RouteGroupRule } from '@rocksoft/shared';
import {
  AlarmChangedEvent,
  AttributesChangedEvent,
  BlindsChangedEvent,
  ButtonPressedEvent,
  CurtainChangedEvent,
  FanSpeedChangedEvent,
  LightChangedEvent,
  MediaStateChangedEvent,
  MoodStartedChanged,
  ReservationStateChangedEvent,
  RoomStateChangedEvent,
  TemperatureChangedEvent,
} from '../shared/events';
import {
  AlarmSignalRStateChange,
  AttributesSignalRStateChange,
  ButtonSignalRStateChanged,
  MediaSignalRStateChanged,
  MobileReservationSignalRStateChanged,
  MoodStarted,
  RoomSignalRStateChange,
  selectAlarmId,
  selectFanspeedId,
  selectLightId,
  selectRoomId,
  selectTemperatureId,
  UpdateFanspeed,
  UpdateLight,
  UpdateTemperature,
  UpsertBlind,
  UpsertCurtain,
} from '../signalr-exports';

/**
 * Creates route group rules for the Virtual Room hub (in-room controls)
 */
export function createVirtualRoomRules(store: Store, zone: NgZone): RouteGroupRule[] {
  return [
    {
      name: 'virtual-room-group',
      urlPattern: /^\/virtual-room/, // matches /virtual-room and /virtual-room/*
      hub: 'virtual-room',
      groupName: 'virtual-room',
      eventHandlers: [
        {
          eventName: 'OnRoomStatusEvent',
          handler: (roomStatus: HotelRoomApiModel) => {
            zone.run(() => {
              store.dispatch(new RoomSignalRStateChange({ id: selectRoomId(roomStatus), changes: roomStatus }));
            });
          },
        },
        {
          eventName: 'AlarmChanged',
          handler: (x: AlarmChangedEvent) => {
            zone.run(() => {
              store.dispatch(
                new AlarmSignalRStateChange({
                  id: selectAlarmId(x.HotelCode, x.Room),
                  changes: {
                    hotelCode: x.HotelCode,
                    room: x.Room,
                    state: x.State,
                    time: x.Time ?? undefined,
                    typeCode: x.TypeCode ?? undefined,
                  },
                })
              );
            });
          },
        },
        {
          eventName: 'BlindsChanged',
          handler: (x: BlindsChangedEvent) => {
            zone.run(() => {
              store.dispatch(new UpsertBlind(x));
            });
          },
        },
        {
          eventName: 'TemperatureChanged',
          handler: (x: TemperatureChangedEvent) => {
            zone.run(() => {
              store.dispatch(new UpdateTemperature({ id: selectTemperatureId(x.HotelCode, x.UnitCode), changes: x }));
            });
          },
        },
        {
          eventName: 'FanSpeedChanged',
          handler: (x: FanSpeedChangedEvent) => {
            zone.run(() => {
              store.dispatch(new UpdateFanspeed({ id: selectFanspeedId(x.HotelCode, x.UnitCode), changes: x }));
            });
          },
        },
        {
          eventName: 'CurtainChanged',
          handler: (x: CurtainChangedEvent) => {
            zone.run(() => {
              store.dispatch(new UpsertCurtain(x));
            });
          },
        },
        {
          eventName: 'LightChanged',
          handler: (x: LightChangedEvent) => {
            zone.run(() => {
              store.dispatch(new UpdateLight({ id: selectLightId(x.HotelCode, x.UnitCode, x.Code ?? ''), changes: x }));
            });
          },
        },
        {
          eventName: 'MediaStateChanged',
          handler: (x: MediaStateChangedEvent) => {
            zone.run(() => {
              store.dispatch(new MediaSignalRStateChanged({ mediaState: x }));
            });
          },
        },
        {
          eventName: 'ButtonPressed',
          handler: (x: ButtonPressedEvent) => {
            zone.run(() => {
              store.dispatch(new ButtonSignalRStateChanged(x));
            });
          },
        },
        {
          eventName: 'MoodStarted',
          handler: (x: MoodStartedChanged) => {
            zone.run(() => {
              store.dispatch(new MoodStarted(x));
            });
          },
        },
        {
          eventName: 'RoomChanged',
          handler: (x: RoomStateChangedEvent) => {
            zone.run(() => {
              store.dispatch(new RoomSignalRStateChange({ id: selectRoomId(x), changes: x }));
            });
          },
        },
        {
          eventName: 'ReservationChanged',
          handler: (x: ReservationStateChangedEvent) => {
            zone.run(() => {
              store.dispatch(new MobileReservationSignalRStateChanged({ mobileReservation: x }));
            });
          },
        },
        {
          eventName: 'AttributesChanged',
          handler: (x: AttributesChangedEvent) => {
            zone.run(() => {
              store.dispatch(new AttributesSignalRStateChange({ changes: x }));
            });
          },
        },
      ],
    },
  ];
}
